import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';

import { ISelectRoleRequest } from '@/types/auth';
import { formattingStringOnlyNumber } from '@/utils/common';
import { mapEsiaIntoDadata } from '@/utils/address';
import { ErrorAlertModule } from '@/store/modules/errors';
import { StatementsModule } from '@/store/modules/statements';
import { getAuthorizationCode, logout, selectRole } from '@/api/auth';
import { findByIdAddressStructure } from '@/api/address';
import {
  Role,
  RoleTypeAuth,
  RoleTypeAuthMap,
  RoleValues,
} from '@/utils/dictionaries';
import { UserEntity, UserOrgDetailsEntity } from '@/domains/User';
import { userService } from '@/services';
import { ErrorText } from '@/constants';

export interface IProfileState {
  isAuthenticated: boolean;
  userFullName: string;
  user: UserEntity | null;
  avatarLetter: string;
  isShowDialogRole: boolean;
  orgDetails: UserOrgDetailsEntity | null;
  isOrganization: boolean;
  roleType: string;
  isBusinessman: boolean;
}

@Module({ dynamic: true, store, name: 'auth' })
class Auth extends VuexModule implements IProfileState {
  isAuthenticated = false;
  user: IProfileState['user'] = null;
  roleType: RoleValues = Role.ANONYMOUSE;
  isShowDialogRole = false;
  isOrganization = false;
  orgDetails: IProfileState['orgDetails'] = null;
  isBusinessman = false;

  get avatarLetter() {
    if (!this.user) throw new Error(ErrorText.NO_USER);

    return this.user && this.user.firstName && this.user.lastName
      ? `${this.user.firstName[0].toUpperCase()}${this.user.lastName[0].toUpperCase()}`
      : '';
  }

  get userFullName() {
    return this.user && this.user.firstName && this.user.lastName
      ? `${this.user.firstName} ${this.user.lastName}`
      : '';
  }

  get paramsEsia() {
    const params: any = {};
    const isOrganization = this.isOrganization;
    const isBusinessman = this.isBusinessman;

    if (isOrganization) {
      if (!this.orgDetails) throw new Error(ErrorText.NO_USER_ORG);

      params.inn = formattingStringOnlyNumber(this.orgDetails.inn);
      if (this.orgDetails.ogrn)
        params.ogrn = formattingStringOnlyNumber(this.orgDetails.ogrn);
    } else {
      if (!this.user) throw new Error(ErrorText.NO_USER);

      params.snils = formattingStringOnlyNumber(this.user.snils);
      params.inn = formattingStringOnlyNumber(this.user.inn);

      if (isBusinessman) {
        if (!this.orgDetails) {
          console.error(ErrorText.NO_USER_ORG);
          return params
        }

        const orgDetails = this.orgDetails;

        if (orgDetails.ogrn) {
          params.ogrn = formattingStringOnlyNumber(orgDetails.ogrn);
        }
      }
    }
    return params;
  }

  @Action
  async getPerson() {
    const addressPLV = await this.findByIdAddress(
      this.user?.currentAddressInfo
    );
    const addressPRG = await this.findByIdAddress(
      this.user?.registerAddressInfo
    );

    return {
      lastName: this.user?.lastName ?? '',
      firstName: this.user?.firstName ?? '',
      patronymic: this.user?.middleName ?? '',
      birthDate: this.user?.birthDate ?? '',
      snils: this.user?.snils || '',
      inn: this.user?.inn || '',
      email: this.user?.contactInfo?.email || '',
      phone: this.user?.contactInfo?.mobilePhone || '',
      address: addressPLV ?? addressPRG ?? {},
    };
  }

  @Action
  async getOrganization() {
    const addressOLG = await AuthModule.findByIdAddress(
      this.orgDetails?.addrDetails?.registerAddressInfo
    );
    const addressOPS = await AuthModule.findByIdAddress(
      this.orgDetails?.addrDetails?.currentAddressInfo
    );

    return {
      name: this.orgDetails?.fullName ?? '',
      ogrn: this.orgDetails?.ogrn ?? '',
      inn: this.orgDetails?.inn ?? '',
      kpp: this.orgDetails?.kpp ?? '',
      email: this.orgDetails?.cttsDetails?.email ?? '',
      phone: this.orgDetails?.cttsDetails?.mobilePhone ?? '',
      address: addressOLG ?? {},
      factAddress: addressOPS ?? {},
    };
  }

  @Mutation
  private SET_USER(user: IProfileState['user']) {
    if (!user) throw new Error(ErrorText.NO_USER);

    this.user = user;
  }

  @Mutation
  public SET_SHOW_DIALOG_ROLE(data: boolean) {
    this.isShowDialogRole = data;
  }

  @Mutation
  private SET_ORG_DETAILS(data: any) {
    this.orgDetails = { ...data };
  }

  @Mutation
  private SET_ROLE(data: RoleValues) {
    this.roleType = data;
    this.isOrganization = data === Role.ORGANIZATION;
    this.isAuthenticated = data !== Role.ANONYMOUSE;
  }

  @Mutation
  private SET_BUSINESSMAN(data: boolean) {
    this.isBusinessman = data;
  }

  @Action({ rawError: true })
  async getResource() {
    ErrorAlertModule.setErrorAlert('');
    try {
      // получаем данные юзера и проставляем признак авторизации
      const data = await userService.getUser();
      this.SET_USER(data);

      switch (data.roleType) {
        case RoleTypeAuth.PERSON:
          this.SET_ROLE(Role.PERSON);
          break;
        case RoleTypeAuth.BUSINESS:
          // если orgDetails нет и roleDetails === 0, выставляем роль "физическое лицо"
          this.SET_ORG_DETAILS(data.orgDetails);
          this.SET_ROLE(Role.PERSON);
          this.SET_BUSINESSMAN(true);
          break;
        case RoleTypeAuth.AGENCY:
        case RoleTypeAuth.LEGAL:
          // если есть orgDetails и roleType === 'ORG', считаем что залогинены под организацией
          if (!data.orgDetails) break;
          this.SET_ORG_DETAILS(data.orgDetails);
          this.SET_ROLE(Role.ORGANIZATION);
          break;
        default:
          // если orgDetails нет и roleDetails > 0, показываем окно выбора роли
          if (data.roleDetails.length > 0) {
            this.SET_SHOW_DIALOG_ROLE(true);
          } else {
            this.selectRole({ type: RoleTypeAuth.PERSON });
          }
      }
    } catch (error) {
      this.SET_ROLE(Role.ANONYMOUSE);
      this.SET_ORG_DETAILS({});
      this.SET_SHOW_DIALOG_ROLE(false);
      this.SET_BUSINESSMAN(false);
    }
  }

  @Action({ rawError: true })
  async selectRole({ type, oid }: any) {
    if (!this.user) throw new Error(ErrorText.NO_USER);

    try {
      const query: ISelectRoleRequest = {
        type: type,
        snils: formattingStringOnlyNumber(this.user.snils),
      };
      if (oid) query.oid = oid;
      // получаем данные юзера и проставляем признак авторизации
      const data = await selectRole(query);
      this.SET_ORG_DETAILS(data);
      // ИП рассматриваем как физ. лицо
      const roleType =
        type === RoleTypeAuth.BUSINESS ? RoleTypeAuth.PERSON : type;
      // @ts-ignore
      this.SET_ROLE(RoleTypeAuthMap[roleType]);
      this.SET_BUSINESSMAN(type === RoleTypeAuth.BUSINESS);
      return true;
    } catch (error) {
      this.SET_ORG_DETAILS({});
      this.SET_ROLE(Role.ANONYMOUSE);
      return false;
    }
  }

  @Action
  async login() {
    ErrorAlertModule.setErrorAlert('');
    try {
      // получаем урл ведущий на страницу авторизации и редиректимся на него
      StatementsModule.deleteFilterLocalStorage();
      window.location.href = await getAuthorizationCode();
    } catch (error) {
      // TODO: текст ошибок перенести на бек
      ErrorAlertModule.setErrorAlert(
        'Не удалось войти в систему. Попробуйте снова, через некоторое время'
      );
    }
  }

  @Action
  async logout() {
    ErrorAlertModule.setErrorAlert('');
    try {
      // получаем урл ведущий в есия для разлогинивания
      const data = await logout();
      StatementsModule.deleteFilterLocalStorage();
      window.location.href = data;
    } catch (error) {
      // TODO: текст ошибок перенести на бек
      ErrorAlertModule.setErrorAlert(
        'Система не отвечает. Попробуйте снова, через некоторое время'
      );
    }
  }

  @Action
  async findByIdAddress(address: any) {
    // физическое лицо:
    // "PLV" - адрес места проживания;
    // "PRG" - адрес места регистрации;
    // организация:
    // "OLG" - юридический адрес;
    // "OPS" - фактический адрес;

    if (!address || !address.fiasCode) return null;
    try {
      const { suggestions } = await findByIdAddressStructure(address.fiasCode);
      return suggestions.map((item: any) => {
        const flat = {
          flat: item.data?.flat || address.flat || null,
          flat_type: 'кв',
        };
        const block = mapEsiaIntoDadata(address);

        return {
          ...item.data,
          ...block,
          ...flat,
          addressStr: item.value,
          // isEsia - показывает компоненту, что адрес предзаполняет из ЕСИЯ
          isEsia: true,
        };
      })[0];
    } catch (error) {
      return null;
    }
  }
}

export const AuthModule = getModule(Auth);
